import axios from 'axios';
import { getConfig } from '../config';

export interface CustomerBalance {
  customerId: string;
  availablePoints: number;
  earned: number;
  redeemed: number;
  tier: string;
}

export interface PointTransaction {
  transactionId: string;
  customerId: string;
  timestamp: string;
  pointsDelta: number;
  remainingPoints: number;
  activityType: string;
  pointType: string;
  earnDate?: string;
  expireAt?: string;
  orderId?: string;
  rewardId?: string;
  campaignId?: string;
}

export interface TransactionHistoryResponse {
  transactions: PointTransaction[];
  lastEvaluatedKey?: string;
}

export interface EarnPointsRequest {
  customerId: string;
  points: number;
  activityType: 'Purchase' | 'Referral' | 'RewardRedemption' | 'Event';
  orderId?: string;
  subDescription?: string;
}

export interface RedeemPointsRequest {
  customerId: string;
  points: number;
}

export interface RedeemPointsResponse {
  transactionId: string;
  newBalance: number;
}

export interface UpdateTierRequest {
  customerId: string;
  newTier: string;
}

export interface UpdateCustomerTierResponse {
  customerId: string;
  tier: string;
}

export interface Reward {
  rewardId: string;
  name: string;
  description: string;
  pointCost: number;
  status: string;
}

export interface CreateRewardRequest {
  name: string;
  description: string;
  pointCost: number;
  status?: string;
}

export interface TierDefinition {
  tier: string;
  rank?: number;
  level?: number;
  minPoints: number;
  benefits: string[] | string;
  multiplier?: number;
  pointMultiplier?: number;
  description?: string;
  displaySettings?: TierDisplaySettings;
}

export interface TierDisplaySettings {
  color: string;
  icon: string;
  displayOrder: number;
}

export interface TierBenefits {
  redemptionEnabled: boolean;
  specialDiscounts: boolean;
  customBenefits: string;
}

export interface CreateTierDefinitionRequest {
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  rank: number;
  minPoints: number;
  benefits: string;
}

export interface UpdateTierDefinitionRequest {
  level?: number;
  minPoints?: number;
  pointMultiplier?: number;
  description?: string;
  benefits?: TierBenefits;
  displaySettings?: TierDisplaySettings;
}

export interface GlobalLoyaltySettings {
  pointsExpiryDays: number;
  expiryNotificationDays: number;
  maxRedemptionPercentage: number;
  minRedemptionPoints: number;
  maxWeeklyInteractionPoint: number;
  autoExpiryEnabled: boolean;
  interactionPoints?: Record<string, number>;
}

export interface UpdateGlobalSettingsRequest {
  pointsExpiryDays?: number;
  expiryNotificationDays?: number;
  maxRedemptionPercentage?: number;
  minRedemptionPoints?: number;
  maxWeeklyInteractionPoint?: number;
  autoExpiryEnabled?: boolean;
  interactionPoints?: Record<string, number>;
}

export class LoyaltyClient {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = getConfig().apiUrl;
  }

  async getCustomerBalance(customerId: string): Promise<CustomerBalance> {
    const url = `${this.baseUrl}/loyalty/customers/${customerId}/balance`;
    console.log('🔍 Fetching customer balance:', url);
    const response = await axios.get<CustomerBalance>(url, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    console.log('✅ Customer balance response:', response.data);
    return response.data;
  }

  async getTransactionHistory(
    customerId: string,
    limit?: number,
    lastEvaluatedKey?: string,
  ): Promise<TransactionHistoryResponse> {
    const params = new URLSearchParams();
    if (limit) {
      params.append('limit', limit.toString());
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const response = await axios.get<TransactionHistoryResponse>(
      `${this.baseUrl}/loyalty/customers/${customerId}/transactions?${params.toString()}`,
      {
        headers: {
          'X-API-KEY': getConfig().apiKey,
        },
      },
    );
    return response.data;
  }

  async earnPoints(request: EarnPointsRequest): Promise<PointTransaction> {
    const response = await axios.post<PointTransaction>(`${this.baseUrl}/loyalty/points/earn`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    return response.data;
  }

  async redeemPoints(request: RedeemPointsRequest): Promise<RedeemPointsResponse> {
    const response = await axios.post<RedeemPointsResponse>(`${this.baseUrl}/loyalty/points/redeem`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    return response.data;
  }

  async updateCustomerTier(request: UpdateTierRequest): Promise<UpdateCustomerTierResponse> {
    const response = await axios.put<UpdateCustomerTierResponse>(`${this.baseUrl}/loyalty/customers/tier`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    return response.data;
  }

  async listRewards(): Promise<Reward[]> {
    const url = `${this.baseUrl}/loyalty/rewards`;
    console.log('🔍 Fetching rewards:', url);
    const response = await axios.get<Reward[]>(url, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    console.log('✅ Rewards response:', response.data);
    return response.data;
  }

  async createReward(request: CreateRewardRequest): Promise<void> {
    await axios.post(`${this.baseUrl}/loyalty/rewards`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
  }

  async listTierDefinitions(): Promise<TierDefinition[]> {
    const response = await axios.get<TierDefinition[]>(`${this.baseUrl}/loyalty/tiers`, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    return response.data;
  }

  async createTierDefinition(request: CreateTierDefinitionRequest): Promise<void> {
    await axios.post(`${this.baseUrl}/loyalty/tiers`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
  }

  async updateTierDefinition(tierId: string, request: UpdateTierDefinitionRequest): Promise<void> {
    await axios.put(`${this.baseUrl}/loyalty/tiers/${tierId}`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
  }

  async deleteTierDefinition(tierId: string): Promise<void> {
    await axios.delete(`${this.baseUrl}/loyalty/tiers/${tierId}`, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
  }

  async getGlobalSettings(): Promise<GlobalLoyaltySettings> {
    const response = await axios.get<GlobalLoyaltySettings>(`${this.baseUrl}/loyalty/admin/settings`, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
    return response.data;
  }

  async updateGlobalSettings(request: UpdateGlobalSettingsRequest): Promise<void> {
    await axios.put(`${this.baseUrl}/loyalty/admin/settings`, request, {
      headers: {
        'X-API-KEY': getConfig().apiKey,
      },
    });
  }
}
